import React from "react";
import Image from "next/image";
import { type ExternalArticleProps, type YoutubeProps, type BlogSimple } from "lib/sanity";
import { Box, AspectRatio, Flex, Text } from "@chakra-ui/react";
import { ResourceCard } from "components/resource-card";
import { LinkCard } from "ui/link-card";
import { blogBlurDataUrl } from "../../../constants/blur-data-urls";

export function ArticleCard({ url, eyebrow, title, description, publishedAt, imageUrl }: ExternalArticleProps) {
  return (
    <ResourceCard
      href={url}
      target="_blank"
      eyebrow={eyebrow}
      titleElement="h3"
      title={title}
      excerpt={description}
      publishedAt={publishedAt}
      h="100%"
      w="300px"
      maxW="100%"
      borderRadius="base"
      image={
        <AspectRatio ratio={2 / 1} pos="relative" width="100%" display={["none", "block"]}>
          <Image
            fill
            sizes="100%"
            src={`${imageUrl}?w=800`}
            alt={`${title}.`}
            placeholder="blur"
            blurDataURL={blogBlurDataUrl}
            style={{
              objectFit: "cover",
            }}
          />
        </AspectRatio>
      }
    />
  );
}

export function YoutubeCard({ url, title, description, uploadDate, imageUrl }: YoutubeProps) {
  return (
    <ResourceCard
      href={url}
      target="_blank"
      eyebrow="Video"
      titleElement="h3"
      title={title}
      excerpt={description}
      publishedAt={uploadDate}
      height="100%"
      w={{ base: "300px", md: "400px" }}
      maxW="100%"
      borderRadius="base"
      image={
        <AspectRatio ratio={2 / 1} pos="relative" width="100%" display={["none", "block"]}>
          <Image
            fill
            sizes="100%"
            src={`${imageUrl}?w=800`}
            alt={`${title}.`}
            placeholder="blur"
            blurDataURL={blogBlurDataUrl}
            style={{
              objectFit: "cover",
            }}
          />
        </AspectRatio>
      }
    />
  );
}

export function BlogCard({ slug, title, excerpt, publishedAt, imageUrl }: BlogSimple) {
  return (
    <ResourceCard
      href={`/blog/${slug}`}
      eyebrow="Hightouch blog"
      titleElement="h3"
      title={title}
      excerpt={excerpt}
      publishedAt={publishedAt}
      h="100%"
      w="300px"
      maxW="100%"
      borderRadius="base"
      image={
        <AspectRatio ratio={2 / 1} pos="relative" width="100%" display={["none", "block"]}>
          <Image
            fill
            sizes="100%"
            src={`${imageUrl}?w=800`}
            alt={`${title}.`}
            placeholder="blur"
            blurDataURL={blogBlurDataUrl}
            style={{
              objectFit: "cover",
            }}
          />
        </AspectRatio>
      }
    />
  );
}

const tierGradient = "linear(90deg, var(--chakra-colors-gray-900) 0%, rgba(25, 26, 28, 0.75) 100%)";

type CertifiedCardProps = {
  hasName?: boolean;
  url: string;
  name: string;
  imageUrl: string;
};

export function CertifiedCard({ hasName, url, name, imageUrl }: CertifiedCardProps) {
  return (
    <LinkCard
      href={url}
      target="_blank"
      linkAriaLabel={`${name}.`}
      bg="white"
      boxSize={{ base: "160px", sm: "180px" }}
      border="1px solid"
      borderColor="base.border"
      borderRadius="base"
      overflow="hidden"
      p={0}
      maxW="100%"
      maxH="100%"
      transition=".15s all ease-in-out"
      _hover={{ borderColor: "gray.400" }}
    >
      <Flex
        align="center"
        bgGradient={tierGradient}
        color="white"
        justify="center"
        fontSize="xs"
        textStyle="uppercaseLight"
        py={1}
        w="100%"
      >
        Certified
      </Flex>

      <Flex
        w="100%"
        h="100%"
        p={hasName ? { base: 5, md: 7 } : 8}
        pb={hasName ? { base: 3, md: 5 } : undefined}
        maxH="80%"
      >
        <Box w="100%" h="100%" pos="relative">
          <Image
            fill
            sizes="100%"
            src={`${imageUrl}?w=800`}
            alt={`${name}.`}
            style={{
              objectFit: "contain",
            }}
          />
        </Box>
      </Flex>

      {hasName && (
        <Text fontSize="xs" fontWeight="semibold" w="100%" textAlign="center" pb={{ base: 3, md: 5 }} px={3}>
          {name}
        </Text>
      )}
    </LinkCard>
  );
}
